import { useEffect, useState } from 'react';
import { engineFetchJson } from '@/lib/engineHttp';
import type { WidgetDefinition, WidgetProps } from '@/types/widget';

interface TaskCounts {
  byStatus: Record<string, number>;
  total: number;
}

const STATUS_ORDER = ['todo', 'in_progress', 'backlog', 'blocked', 'review', 'done', 'cancelled'];

const STATUS_COLORS: Record<string, string> = {
  todo: 'var(--sp-text-muted)',
  in_progress: 'var(--sp-accent)',
  backlog: 'var(--sp-text-dim)',
  blocked: 'var(--sp-error)',
  review: 'var(--sp-warn)',
  done: 'var(--sp-ok)',
  cancelled: 'var(--sp-text-dim)',
};

function BoardSummaryWidget({ context }: WidgetProps) {
  const [counts, setCounts] = useState<TaskCounts | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    void engineFetchJson<{ by_status?: Record<string, number> }>('/tasks/summary')
      .then((d) => {
        const byStatus: Record<string, number> = d.by_status ?? {};
        const total = Object.values(byStatus).reduce((sum: number, v) => sum + (v as number), 0);
        setCounts({ byStatus, total });
        setError(null);
      })
      .catch((e) => {
        setError(`Failed to load: ${e.message}`);
      });
  }, [context.authToken]);

  return (
    <div className="sp-widget">
      <div className="sp-widget-header">
        <span>Board Summary</span>
        <a href="/board" style={{ fontSize: 11, fontWeight: 400, textTransform: 'none', letterSpacing: 0 }}>
          Open board
        </a>
      </div>
      <div className="sp-widget-body" style={{ padding: '4px 0' }}>
        {error ? (
          <div style={{ padding: 12 }}>
            <div style={{ color: 'var(--sp-error)', fontSize: 12 }}>{error}</div>
            <div className="mono" style={{ color: 'var(--sp-text-dim)', fontSize: 11, marginTop: 4 }}>
              GET /api/v1/tasks/summary
            </div>
          </div>
        ) : counts ? (
          <div>
            <div style={{ padding: '0 12px 8px', borderBottom: '1px solid var(--sp-border)' }}>
              <span style={{ fontSize: 24, fontWeight: 700 }}>{counts.total}</span>
              <span style={{ fontSize: 12, color: 'var(--sp-text-muted)', marginLeft: 6 }}>total tasks</span>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(100px, 1fr))', gap: 2, padding: '8px 12px' }}>
              {STATUS_ORDER.filter((s) => counts.byStatus[s] !== undefined).map((status) => (
                <div key={status} style={{ padding: '4px 0' }}>
                  <div className="mono" style={{ fontSize: 10, color: 'var(--sp-text-dim)', textTransform: 'uppercase' }}>
                    {status.replace('_', ' ')}
                  </div>
                  <div style={{ fontSize: 18, fontWeight: 700, color: STATUS_COLORS[status] ?? 'var(--sp-text)' }}>
                    {counts.byStatus[status]}
                  </div>
                </div>
              ))}
            </div>
          </div>
        ) : (
          <div style={{ padding: 12, color: 'var(--sp-text-muted)', fontSize: 12 }}>Loading...</div>
        )}
      </div>
    </div>
  );
}

export const boardSummaryWidget: WidgetDefinition = {
  id: 'board-summary',
  name: 'Board Summary',
  description: 'Task counts by status with click-through to /board.',
  icon: 'layout-dashboard',
  category: 'workspace',
  scope: 'both',
  defaultSize: { w: 6, h: 4 },
  minSize: { w: 3, h: 3 },
  component: BoardSummaryWidget,
};
